import { memo, useState, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { Upload, Image, X, CheckCircle, Loader2, Layers, AlertTriangle, RotateCcw, PenLine, Camera } from 'lucide-react';
import ImageEditModal from '../imageEdit/ImageEditModal';
import NodeLimitWarning from './NodeLimitWarning';
import { outputFromPreset } from '../../utils/imageEdit';
import { useWorkflowStore } from '../../store/workflowStore';
import { useBatchStore } from '../../store/batchStore';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import { uploadToR2 } from '../../utils/uploadToR2';
import { describeUploadError } from '../../utils/uploadLimits';
import { toast } from 'sonner';

const UploadImageNode = memo(({ id, data, selected }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // null | 0-100
  // AP-4 · «Editar imagen»: guarda una imagen NUEVA y la lleva al Canvas.
  const [editSource, setEditSource] = useState(null);
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);

  const handleFileChange = useCallback((file) => {
    if (!file) return;

    // 1) Show an immediate local preview (in-memory only — NOT persisted to project)
    const reader = new FileReader();
    reader.onload = (e) => {
      updateNodeData(id, {
        file,
        fileName: file.name,
        preview: e.target.result, // base64 preview (kept only in memory; saveProject nulls it)
        type: 'uploaded',
        hasOutput: false,          // not connectable until R2 upload completes
        outputUrl: null,
        uploading: true,
        uploadError: null,
      });
    };
    reader.readAsDataURL(file);

    // 2) Upload to R2 in the background and replace outputUrl with the public URL
    setUploadProgress(0);
    uploadToR2(file, { onProgress: setUploadProgress })
      .then(({ url, r2Key, filename }) => {
        updateNodeData(id, {
          hasOutput: true,
          outputUrl: url,
          r2Key,
          uploading: false,
          uploaded: true,
          uploadError: null,
        });
        addAsset({
          id: `asset_${Date.now()}`,
          type: 'image',
          tool: 'upload',
          filename: filename || file.name,
          url,
          r2Key,
          createdAt: new Date().toISOString(),
        });
      })
      .catch((err) => {
        console.error('R2 upload failed:', err);
        const message = describeUploadError(err);
        toast.error(message, { position: 'bottom-right', duration: 8000 });
        updateNodeData(id, { uploading: false, uploadError: message });
      })
      .finally(() => {
        setUploadProgress(null);
      });
  }, [id, updateNodeData, addAsset]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleFileChange(file);
    }
  }, [handleFileChange]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const clearImage = useCallback(() => {
    updateNodeData(id, {
      file: null,
      fileName: null,
      preview: null,
      hasOutput: false,
      outputUrl: null,
      r2Key: null,
      uploading: false,
      uploaded: false,
      uploadError: null,
    });
    setUploadProgress(null);
  }, [id, updateNodeData]);

  const handleSourcePick = useCallback(() => {
    setSourcePickerHandler((result) => {
      if (result.file) {
        handleFileChange(result.file);
      } else if (result.url) {
        updateNodeData(id, {
          file: null,
          fileName: result.fileName,
          preview: result.url,
          hasOutput: true,
          outputUrl: result.url,
          type: 'gallery',
          fromGallery: true,
          uploadError: null,
        });
      }
    });
    openSourcePicker(id, ['image'], 'image/jpeg,image/jpg,image/png,image/webp');
  }, [id, openSourcePicker, handleFileChange, updateNodeData]);

  return (
    <div className={`node-container upload-node ${selected ? 'selected' : ''}`}>
      {/* Batch badge — visible only when this node is the batch INICIO */}
      <BatchBadge nodeId={id} />
      {/* Header */}
      <div className="node-header">
        <div className="node-header-left">
          <Upload size={16} />
          <span>Upload Image</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)}>
          <X size={14} />
        </button>
      </div>
      
      {/* Content */}
      <div className="node-content">
        {(data.preview || data.outputUrl) ? (
          <div className="node-preview-container">
            <img 
              src={data.preview || data.outputUrl} 
              alt="Preview" 
              className="node-preview-image"
              onError={(e) => {
                console.error('Image load error:', e);
                e.target.style.display = 'none';
              }}
            />
            <div className="node-preview-info">
              {data.uploading ? (
                <>
                  <Loader2 size={12} className="spin-icon text-indigo-400" />
                  <span className="node-filename">
                    {uploadProgress != null ? `Subiendo... ${uploadProgress}%` : 'Subiendo...'}
                  </span>
                </>
              ) : data.uploadError ? (
                <span className="upload-error-line" role="alert" title={data.uploadError} data-testid="upload-image-error">
                  <AlertTriangle size={12} />
                  <span>No se pudo subir</span>
                  {data.file && (
                    <button type="button" className="upload-retry-btn nodrag" onClick={() => handleFileChange(data.file)}>
                      <RotateCcw size={12} /> Reintentar
                    </button>
                  )}
                </span>
              ) : (
                <>
                  <CheckCircle size={12} className="text-emerald-400" />
                  <span className="node-filename">{data.fileName}</span>
                </>
              )}
            </div>
            {/* AP-14 · aviso si no cumple los límites del modelo conectado */}
            {data.outputUrl && !data.uploading && (
              <NodeLimitWarning nodeId={id} url={data.outputUrl} kind="image"
                onAdapt={(preset) => setEditSource({
                  url: data.outputUrl, label: data.fileName || 'Imagen', tool: 'tamano',
                  params: { output: outputFromPreset(preset) }, adapt: true,
                })} />
            )}
            <div className="node-preview-actions">
              {data.outputUrl && !data.uploading && (
                <button type="button" className="node-clear-btn node-edit-btn nodrag" data-testid="upload-image-edit"
                  onClick={() => setEditSource({ url: data.outputUrl, label: data.fileName || 'Imagen' })}>
                  <PenLine size={12} /> Editar
                </button>
              )}
              <button className="node-clear-btn" onClick={clearImage} disabled={data.uploading}>
                <X size={12} /> Limpiar
              </button>
            </div>
          </div>
        ) : (
          <div
            className={`node-upload-zone ${isDragging ? 'dragging' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <Image size={24} className="upload-icon" />
            <p>Arrastra una imagen o</p>
            <span className="node-load-row">
              <button type="button" className="node-load-btn" onClick={handleSourcePick} data-testid="load-btn-upload-image">
                <Upload size={14} /> Cargar
              </button>
              {/* AP-11 · directo a la cámara */}
              <button type="button" className="node-load-btn nodrag" onClick={() => { handleSourcePick(); useWorkflowStore.getState().setSourcePickerStep('camera'); }}
                title="Sacar una foto con la cámara" data-testid="camera-btn-upload-image">
                <Camera size={14} /> Cámara
              </button>
            </span>
          </div>
        )}
      </div>
      
      {editSource && (
        <ImageEditModal
          source={editSource}
          defaultToCanvas={!editSource.adapt}
          canvasOption={!editSource.adapt}
          near={useWorkflowStore.getState().nodes.find((n) => n.id === id)?.position || null}
          // AP-14 · «Adaptar»: la versión ajustada reemplaza la del nodo (el original queda en la Galería)
          onSaved={editSource.adapt ? (out) => updateNodeData(id, {
            outputUrl: out.url, preview: out.url, fileName: out.name, hasOutput: true, file: null, uploadError: null,
          }) : undefined}
          onClose={() => setEditSource(null)}
        />
      )}

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

UploadImageNode.displayName = 'UploadImageNode';

/**
 * BatchBadge — small overlay indicator shown on the INICIO Upload node
 * when batch mode is active. Reports source count, running progress,
 * or completion summary depending on batch status.
 */
const BatchBadge = ({ nodeId }) => {
  const batchMode = useBatchStore((s) => s.batchMode);
  const startNodeId = useBatchStore((s) => s.startNodeId);
  const sources = useBatchStore((s) => s.sources);
  const items = useBatchStore((s) => s.items);
  const status = useBatchStore((s) => s.status);
  const currentIndex = useBatchStore((s) => s.currentIndex);

  if (!batchMode || startNodeId !== nodeId) return null;

  // Running
  if (status === 'running') {
    const currentItem = items[currentIndex];
    return (
      <div className="node-batch-badge running" title={currentItem?.sourceLabel ? `Procesando: ${currentItem.sourceLabel}` : 'Lote en ejecución'} data-testid="batch-badge-running">
        {currentItem?.sourceUrl && (
          <img src={currentItem.sourceUrl} alt="" className="node-batch-badge-thumb" data-testid="batch-badge-thumb" />
        )}
        <Layers size={11} />
        <span>Ítem {Math.max(0, currentIndex) + 1}/{items.length}</span>
      </div>
    );
  }

  // Finished (completed / failed / cancelled)
  if (items.length > 0 && (status === 'completed' || status === 'failed' || status === 'cancelled')) {
    const ok = items.filter((i) => i.status === 'completed').length;
    const bad = items.filter((i) => i.status === 'failed').length;
    return (
      <div className={`node-batch-badge ${bad > 0 ? 'partial' : 'done'}`} title={`Lote finalizado: ${ok} OK · ${bad} fallidos`}>
        <Layers size={11} />
        <span>✓{ok} · ✕{bad}</span>
      </div>
    );
  }

  // Configured but not run yet
  if (sources.length > 0) {
    return (
      <div className="node-batch-badge configured" title={`Lote configurado con ${sources.length} imagen(es)`}>
        <Layers size={11} />
        <span>{sources.length} lote</span>
      </div>
    );
  }

  // Marked as start but no sources yet
  return (
    <div className="node-batch-badge empty" title="Este nodo está marcado como INICIO del lote (sin fuentes todavía)">
      <Layers size={11} />
      <span>INICIO</span>
    </div>
  );
};

export default UploadImageNode;
